import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { BoxFlex, RadioCard, Ribbon } from '@woovi/ui';
import {
  basesPointToDecimal,
  basesPointToPercentage,
  convertToMoney,
} from '@woovi/utils';

import type { PaymentPixCreditOptions_charge$key } from './__generated__/PaymentPixCreditOptions_charge.graphql.ts';
import PaymentSectionChip from '../PaymentSectionChip.tsx';

import type { ReactNode } from 'react';

type PaymentPixCreditOptionsProps = {
  charge: PaymentPixCreditOptions_charge$key;
};
const PaymentPixCreditOptions = (
  props: PaymentPixCreditOptionsProps,
): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();

  const charge = useFragment<PaymentPixCreditOptions_charge$key>(
    graphql`
      fragment PaymentPixCreditOptions_charge on Charge {
        valueWithDiscount
        availablePaymentMethods
        availablePaymentMethodsPixCredit {
          options {
            valueInCash
            installmentNumber
            installmentValue
            discount
          }
        }
      }
    `,
    props.charge,
  );

  const { availablePaymentMethodsPixCredit } = charge;
  const { options } = availablePaymentMethodsPixCredit;

  const discountInDecimal = basesPointToDecimal(options[0].discount);
  const discountInt = options[0].valueInCash * discountInDecimal;
  const discountValue = convertToMoney(discountInt);

  const renderPixCreditOption = (option): ReactNode => {
    return (
      <RadioCard
        cardProps={{
          sx: {
            flex: 1,
          },
        }}
        key={`pixCreditCard-${option.installmentNumber}`}
        radioTestId={`pixCreditCard-${option.installmentNumber}`}
        value={option.installmentNumber}
      >
        <Typography sx={{ fontSize: 20 }}>
          {convertToMoney(option.valueInCash)} +
          <span style={{ fontWeight: 'bold' }}>
            {option.installmentNumber}x
          </span>{' '}
          {convertToMoney(option.installmentValue)}
        </Typography>
        <BoxFlex sx={{ flexDirection: 'column', mt: 1 }}>
          {t('Total value')}: {convertToMoney(charge.valueWithDiscount)}
        </BoxFlex>
      </RadioCard>
    );
  };

  return (
    <>
      <BoxFlex
        sx={{ position: 'relative', flexDirection: 'column', width: '100%' }}
      >
        <PaymentSectionChip>
          {options[0].discount > 0 ? t('Discount Pix') : t('Pix')}
        </PaymentSectionChip>
        <RadioCard
          cardProps={{
            sx: {
              flex: 1,
            },
          }}
          radioTestId={`pixCreditCard-${options[0].installmentNumber}`}
          value={options[0].installmentNumber}
        >
          <Typography sx={{ fontSize: 20 }}>
            <span style={{ fontWeight: 'bold' }}>1x</span>{' '}
            {convertToMoney(options[0].valueInCash)}
          </Typography>
          {options[0].discount > 0 && (
            <Stack spacing={1}>
              <Typography color={theme.palette.primary.main}>
                {t('Get {{discountPercentage}} off', {
                  discountPercentage: basesPointToPercentage(
                    options[0].discount,
                  ),
                })}
              </Typography>
              <Ribbon color={theme.palette.tertiary.main}>
                <Typography color={'white'}>
                  {t('Save {{discountValue}} with Pix right away', {
                    discountValue,
                  })}
                </Typography>
              </Ribbon>
            </Stack>
          )}
        </RadioCard>
      </BoxFlex>

      <BoxFlex
        sx={{ position: 'relative', flexDirection: 'column', width: '100%' }}
      >
        <PaymentSectionChip>{t('Pix + Credit Card')}</PaymentSectionChip>
        {options?.map((option, i) => {
          if (i === 0) {
            return null;
          }

          return renderPixCreditOption(option);
        })}
      </BoxFlex>
    </>
  );
};

export default PaymentPixCreditOptions;
